/**
 * The attention stack — **ordered by consequence, not by recency**, and pure.
 *
 * `screens.md` §2: *"Items stack, ordered by consequence and numbered so the user knows
 * the list ends: recall → domain error / disputed prefix → billing → everything else."*
 * The design's own rationale note spells the full order — *recall, then domain error,
 * then failed import, then billing, then prefix, then usage* — and Phase 2 has subjects
 * for exactly two of those, plus one the design does not name.
 *
 * ## What has a subject in Phase 2, and what does not
 *
 * F7 enumerates the states with no subject so they are not met one at a time. **Failed
 * imports, billing and usage are absent here, and their absence is the feature**: no
 * entitlement gate exists (measured), imports are Phase 6's, and §4 forbids the dashboard
 * reading `scan_events`. **Domain error left that list at U16**, answered by the watch's
 * `consecutive_probe_failures` against `SUSTAINED_PROBE_FAILURES`; nothing demotes a live
 * row (Q1), so the counter is the only evidence a serving domain has stopped serving.
 * Rendering any of them would mean inventing data, and `acceptance-tests.md`'s governing
 * line is *"nothing invented; open questions flagged rather than filled in."*
 *
 * What does have a subject: an **active recall** (`recalls.list`), a **disputed prefix**
 * (`companyPrefixes.list`), and **draft products** — the third is not in the design's
 * list because the design assumed a catalogue that already exists, and a draft is the one
 * thing on this screen a Phase 2 user can act on today.
 *
 * ## Why this is a function and not JSX
 *
 * So the ordering can be tested without a browser, and — the part that matters — so the
 * **absence** can be. An attention surface that always shows something is one nobody
 * reads, which is F3's always-on advisory one screen earlier. `attentionStack` returning
 * `[]` for a healthy org is an assertion a test can make about a value rather than about
 * a rendered tree.
 */

import { SUSTAINED_PROBE_FAILURES } from "@packagepage/shared";

import {
  BILLING_STATE_TONES,
  billingAttention,
  dayName,
  type BillingStanding,
} from "./billing-states.js";
import { trialAttention, type TrialStanding } from "./trial-states.js";
import {
  METER_LABELS,
  METER_ORDER,
  count,
  percentOf,
  readMeter,
  type UsageCounter,
} from "./usage-meter.js";

/** How loud an item is allowed to be. Maps to `Surface`'s tone; `recall` is reserved. */
export type AttentionTone = "recall" | "warn" | "neutral";

export interface AttentionItem {
  /** Stable across renders and unique within a stack — the React key and the test's handle. */
  readonly id: string;
  /**
   * Which of the card's checks this item is the failure of.
   *
   * `"drafts"` is gone: the card is bounded at one row per check and a draft product is
   * not one of them. `checks.ts` maps these to checks, and a kind matching no check would
   * be counted in the header and absent from the footer.
   */
  readonly kind:
    | "recall"
    | "domain-failing"
    | "prefix-disputed"
    | "prefix-missing"
    | "prefix-unverified"
    | "prefix-pending"
    | "billing-grace"
    | "billing-degraded"
    | "trial-closing"
    | "usage-near-limit"
    | "usage-over-limit";
  readonly tone: AttentionTone;
  readonly title: string;
  /**
   * What printed codes are doing right now, first.
   *
   * `acceptance-tests.md`: *"Every banner and error answers 'what are printed codes doing
   * right now?' before anything else."* Every string below opens on resolution behaviour
   * and reaches the remedy second.
   */
  readonly body: string;
  /** The screen that answers for this item, where one exists. */
  readonly href?: string;
}

/** Just what this screen reads. Structural, so a test can build one without the server. */
export interface AttentionInput {
  readonly recalls: readonly {
    id: string;
    gtin14: string;
    status: string;
    scope: string;
    lots: readonly string[];
  }[];
  /**
   * Live domains and their watch counters — `domains.list`, as `screenStateFor` reads it.
   *
   * The whole rows rather than a precomputed flag, so the threshold is applied in one place
   * and this function cannot disagree with the Domains screen about what "failing" means.
   */
  readonly domains: readonly {
    id: string;
    hostname: string;
    status: string;
    consecutive_probe_failures: number;
    printed_sku_count: number;
  }[];
  readonly prefixes: readonly { id: string; prefix: string; verification: string }[];
  /**
   * How many products are drafts — **a count, not a page of rows.**
   *
   * This took an array of products until c34, and counted `status === "draft"` in it.
   * That was correct while the list returned everything and became wrong the moment the
   * list was paginated: a brand with 900 products would have had its drafts counted out
   * of the first hundred rows, and the number on the dashboard would have been quietly,
   * plausibly too small.
   *
   * Taking the count rather than the rows makes the mistake unrepresentable here — this
   * function cannot miscount something it is never given. The counting happens in one
   * `GROUP BY` on the server, which is the only place that can see the whole catalogue.
   */
  readonly draftCount: number;
  /**
   * The current window's counters — `usage.current`. Absent or empty before the first
   * rollup, and then no usage item can appear.
   */
  readonly usage?: readonly UsageCounter[];
  /** A plan change inside the window that requests count from — `countedFromOf`. */
  readonly usageCountedFrom?: Date | null;
  /** `orgs.current`'s billing facts. Absent until it answers, and then no billing item appears. */
  readonly billing?: BillingStanding & TrialStanding;
  /** The clock the billing row's dates read. Defaults to now; tests pass a fixture. */
  readonly now?: Date;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The stack, in consequence order. Empty for an organization with nothing to answer for.
 *
 * The order is the array order below and is not sorted by anything derived — a
 * comparator would let a future field decide precedence, and precedence here is a product
 * decision rather than a property of the data.
 */
export function attentionStack(input: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = [];

  // 1 — Recall. The only place `--pp-recall` is permitted on this screen, and it takes
  // the top slot whenever it appears.
  for (const recall of input.recalls) {
    if (recall.status !== "active") continue;
    const scope =
      recall.scope === "gtin"
        ? "every unit of that code"
        : `${plural(recall.lots.length, "lot", "lots")} ${recall.lots.join(", ")}`;
    items.push({
      id: `recall:${recall.id}`,
      kind: "recall",
      tone: "recall",
      title: `A recall notice is live on ${recall.gtin14}`,
      body:
        `Scans of ${scope} are landing on the notice instead of the product page, and ` +
        `will keep doing so until the recall is lifted. Everything else on this code ` +
        `resolves normally.`,
      href: `/recalls/${recall.id}`,
    });
  }

  // 2 — A live domain that has stopped answering. SECOND, which is `components.md`'s own
  // order — recall -> domain error -> billing -> everything else — and not a judgement made
  // here. `warn`, never `error`: scans still resolve, on the platform host.
  for (const domain of input.domains) {
    if (domain.status !== "live") continue;
    if (domain.consecutive_probe_failures < SUSTAINED_PROBE_FAILURES) continue;
    const printed =
      domain.printed_sku_count === 1
        ? "1 product has print files carrying this address"
        : `${String(domain.printed_sku_count)} products have print files carrying this address`;
    items.push({
      id: `domain:${domain.id}`,
      kind: "domain-failing",
      tone: "warn",
      title: `${domain.hostname} has stopped answering`,
      // Resolution behaviour first, remedy second — `acceptance-tests.md`'s rule, and the
      // reason the count is in the first half rather than the last: what the reader needs
      // is whether the codes already on shelves still work.
      body:
        `Scans of codes printed with this address are not reaching us through it, and ` +
        `${printed}. Point the CNAME back at our edge, or check that the host is up — ` +
        `nothing needs re-printing either way.`,
    });
  }

  // 3 — A disputed prefix. `warn`, never `error`: nothing has stopped resolving, and
  // §12's ladder is a review rather than a failure.
  for (const prefix of input.prefixes) {
    if (prefix.verification !== "disputed") continue;
    items.push({
      id: `prefix:${prefix.id}`,
      kind: "prefix-disputed",
      tone: "warn",
      title: `Prefix ${prefix.prefix} is disputed`,
      // The third of c29's sites, and the one its Drift entry did not count — c91b. It
      // said "both dispute bodies" and there were three; this card was written later, in
      // the weakened voice, and inherited it. It is also the site the upload path serves
      // most directly: this loop is over prefixes this organisation holds, so the reader
      // has a row and can submit against it.
      body:
        `Codes you have already printed keep resolving — a dispute does not take ` +
        `anything down. What it does hold is print files: artwork for a GTIN under a ` +
        `disputed prefix is blocked until it is resolved. Submit your GS1 licence ` +
        `certificate for this prefix and we will review both claims.`,
    });
  }

  // Billing — after domain error and disputed prefix in `components.md`'s order. `warn` at
  // most: nothing a brand owes is broken for its customers (README invariant 1).
  const billing =
    input.billing === undefined
      ? null
      : billingAttention(input.billing, input.now ?? new Date());
  if (billing !== null && input.billing !== undefined) {
    items.push({
      id: "billing",
      kind: billing.kind,
      tone: BILLING_STATE_TONES[input.billing.billingStatus],
      title: billing.title,
      body: billing.body,
      href: "/settings/billing",
    });
  }
  // The trial's last three days, on the billing check's row. A trial holds no subscription, so
  // it never meets a grace or degraded row above.
  const trial =
    input.billing === undefined || billing !== null
      ? null
      : trialAttention(
          input.billing,
          input.now ?? new Date(),
          input.usage?.find((counter) => counter.metric === "gtins_active")?.value ??
            null,
        );
  if (trial !== null) {
    items.push({
      id: "billing",
      kind: trial.kind,
      tone: "warn",
      title: trial.title,
      body: trial.body,
      href: "/settings/billing",
    });
  }

  // 4 — Every other state `trustRefusal` refuses, one kind each (§3b). After billing in
  // `components.md`'s order. Not "codes resolve now", as the
  // Decision drafted it: a draft's GTIN answers 404 UNKNOWN_GTIN, measured on L — Drift U25.
  if (input.prefixes.length === 0) {
    items.push({
      id: "prefix:none",
      kind: "prefix-missing",
      tone: "warn",
      title: "No company prefix is claimed yet",
      body:
        `None of your codes answers scans yet; publishing waits for a verified prefix. ` +
        `Claim yours under Settings → Organisation, then upload your GS1 licence ` +
        `certificate there.`,
    });
  }
  for (const prefix of input.prefixes) {
    if (prefix.verification === "unverified") {
      items.push({
        id: `prefix:${prefix.id}`,
        kind: "prefix-unverified",
        tone: "warn",
        title: `Prefix ${prefix.prefix} needs its licence certificate`,
        body:
          `Codes under ${prefix.prefix} do not answer scans yet; publishing them waits for ` +
          `the prefix to be verified. Upload your GS1 licence certificate for it under ` +
          `Settings → Organisation.`,
      });
    }
    if (prefix.verification === "pending") {
      // `neutral`: nothing is on the tenant's desk — `components.md`, "colouring it amber
      // teaches them to distrust amber". The first emitter of this tone since 2026-08-11.
      items.push({
        id: `prefix:${prefix.id}`,
        kind: "prefix-pending",
        tone: "neutral",
        title: `Prefix ${prefix.prefix} is with a reviewer`,
        body:
          `Codes under ${prefix.prefix} do not answer scans yet; publishing them waits ` +
          `for the review. Nothing is needed from you — the answer appears under ` +
          `Settings → Organisation, and we cannot email you when it does.`,
      });
    }
  }

  // 5 — Usage, last in `components.md`'s order. One item for the check however many meters
  // are near, because the card holds one row per check. `warn`: nothing stops at a limit.
  const usageItem = usageAttention(input.usage ?? [], input.usageCountedFrom ?? null);
  if (usageItem !== null) items.push(usageItem);

  /*
   * **Drafts is gone from this stack, ruled 2026-08-11.**
   *
   * `components.md` bounds the attention card at six rows, one per check, and says
   * "every attention item is exactly one of these six failing". A draft product is not
   * one of the six — it is ordinary work rather than a check reporting a condition, and
   * this file's own docblock always admitted the design never listed it.
   *
   * Keeping it would have broken the card's arithmetic in the visible way: the header
   * counts failing checks and the footer names the clear ones, and an item belonging to
   * no check is counted in neither. The draft count already has a home on the catalogue
   * card, next to the other three statuses, which is where a count belongs.
   */

  return items;
}

function joinLabels(labels: readonly string[]): string {
  return labels.length <= 1
    ? (labels[0] ?? "")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1] ?? ""}`;
}

/** The usage check's one item, or `null` when every meter is under 80%. */
function usageAttention(
  counters: readonly UsageCounter[],
  countedFrom: Date | null,
): AttentionItem | null {
  const meters = METER_ORDER.flatMap((metric) =>
    counters.filter((counter) => counter.metric === metric).map(readMeter),
  );
  const over = meters.filter((meter) => meter.state === "over-limit");
  const near = meters.filter((meter) => meter.state === "near-limit");
  const flagged = over.length > 0 ? over : near;
  if (flagged.length === 0) return null;

  const figures = flagged
    .map((meter) => `${METER_LABELS[meter.metric]} ${meter.figure}`)
    .join(", ");
  const names = joinLabels(flagged.map((meter) => METER_LABELS[meter.metric]));
  const single = flagged.length === 1 ? flagged[0] : undefined;
  const title =
    single !== undefined && single.limit !== null
      ? over.length > 0
        ? `${METER_LABELS[single.metric]} are past your plan's ${count(single.limit)}`
        : `${METER_LABELS[single.metric]} are at ${String(percentOf(single.value, single.limit))}% of your plan`
      : over.length > 0
        ? `${names} are past your plan's limits`
        : `${names} are near your plan's limits`;
  return {
    id: "usage",
    kind: over.length > 0 ? "usage-over-limit" : "usage-near-limit",
    tone: "warn",
    title,
    body:
      `Every printed code keeps resolving. ` +
      `${countedFrom === null ? "This month" : `Since ${dayName(countedFrom)}`}: ${figures}. Billing & usage shows ` +
      `the counts and the plans above yours.`,
    href: "/settings/billing",
  };
}

/** The catalogue counts the right-hand column shows. Neutral by construction — no meters. */
export interface CatalogueCounts {
  readonly published: number;
  readonly draft: number;
  readonly archived: number;
  readonly unavailable: number;
}

/**
 * Read from the server's own `GROUP BY`, **never counted from a page of rows.**
 *
 * The previous version filtered the array the list returned, which was right while the
 * list returned the whole catalogue and wrong the moment c34 paginated it. The failure
 * was not that the number was missing — it was that the number was *present, plausible
 * and too small*, which is the only kind of wrong a dashboard panel can be without
 * anybody noticing.
 *
 * A status nobody has used is absent from the server's result rather than zero, because
 * `GROUP BY` has no reason to invent a row; `?? 0` is where that becomes a number.
 *
 * **Not a usage meter.** F7: the meters' scan half has no subject in Phase 2 (rollups are
 * Phase 7 and §4 forbids the dashboard reading `scan_events`), and the GTIN half is an
 * entitlement question with no gate behind it. What is left is a count of what exists,
 * which is true, useful and claims nothing about a limit.
 *
 * The vocabulary is the shopper's where the shopper has one: a tombstoned product is
 * **"No longer available"** here exactly as it is on the consumer page, and the words
 * "tombstone" and "delete" appear in this product's UI nowhere at all.
 */
export function catalogueCounts(
  counts: Readonly<Partial<Record<string, number>>>,
): CatalogueCounts {
  return {
    published: counts["published"] ?? 0,
    draft: counts["draft"] ?? 0,
    archived: counts["archived"] ?? 0,
    unavailable: counts["tombstoned"] ?? 0,
  };
}
